"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDefaultThreshold = exports.getConfusionMatricesClassification = exports.getOptimalThresholdByCostRatio = exports.getConfusionMatrixByThreshold = void 0;
var ModelMetrics_1 = require("../components/model-results/type/ModelMetrics");
function _getConfusionMatrix(tn, tp, fn, fp, threshold) {
    var trueNegativeRate = tn / (tn + fp);
    var truePositiveRate = tp / (tp + fn);
    var positivePredictiveValue = tp / (tp + fp);
    var negativePredictiveValue = tn / (tn + fn);
    var getFScore = function (beta) {
        var betaSquared = beta * beta;
        return ((1 + betaSquared) * tp) / ((1 + betaSquared) * tp + betaSquared * fp + fn);
    };
    return {
        threshold: threshold,
        trueNegative: tn,
        falseNegative: fn,
        truePositive: tp,
        falsePositive: fp,
        accuracy: (tn + tp) / (tn + tp + fn + fp),
        trueNegativeRate: trueNegativeRate,
        truePositiveRate: truePositiveRate,
        falsePositiveRate: 1 - trueNegativeRate,
        falseNegativeRate: 1 - truePositiveRate,
        positivePredictiveValue: positivePredictiveValue,
        negativePredictiveValue: negativePredictiveValue,
        f1Score: getFScore(1),
        informedness: truePositiveRate + trueNegativeRate - 1,
        markedness: positivePredictiveValue + negativePredictiveValue - 1,
        matthewsCorrelationCoefficient: (tp * tn - fp * fn) / Math.sqrt((tp + fp) * (tp + fn) * (tn + fp) * (tn + fn)),
    };
}
function _getAllPositiveMatrix(tns, tps, fns, fps, threshold) {
    var actualPositives = tps[0] + fns[0];
    var actualNegatives = tns[0] + fps[0];
    return _getConfusionMatrix(0, actualPositives, 0, actualNegatives, threshold);
}
function _getAllNegativeMatrix(tns, tps, fns, fps, threshold) {
    var actualPositives = tps[0] + fns[0];
    var actualNegatives = tns[0] + fps[0];
    return _getConfusionMatrix(actualNegatives, 0, actualPositives, 0, threshold);
}
function _getConfusionMatrixByIndex(index, tns, tps, fns, fps, threshold) {
    return _getConfusionMatrix(tns[index], tps[index], fns[index], fps[index], threshold);
}
function _binarySearch(sortedArray, value) {
    var low = 0;
    var high = sortedArray.length - 1;
    if (value < sortedArray[low]) {
        return -1;
    }
    else if (value > sortedArray[high]) {
        return -(sortedArray.length + 1);
    }
    while (low <= high) {
        var middle = (low + high) >>> 1;
        var middleValue = sortedArray[middle];
        if (middleValue < value) {
            low = middle + 1;
        }
        else if (middleValue > value) {
            high = middle - 1;
        }
        else if (middleValue === value) {
            return middle;
        }
    }
    return -(low + 1); // key not found.
}
function getConfusionMatrixByThreshold(confusionMatrices, threshold) {
    if (!confusionMatrices || !confusionMatrices.thresholds || !confusionMatrices.thresholds.length) {
        return;
    }
    else if (threshold < 0 || threshold > 1) {
        return;
    }
    var thresholds = confusionMatrices.thresholds, tns = confusionMatrices.tns, tps = confusionMatrices.tps, fns = confusionMatrices.fns, fps = confusionMatrices.fps;
    var index = _binarySearch(thresholds, threshold);
    if (index >= 0) {
        // we have the exact threshold that was specified
        return _getConfusionMatrixByIndex(index, tns, tps, fns, fps, threshold);
    }
    else {
        // we don't have the exact threshold so we need to give our best guess
        var insertionIndex = -index - 1;
        if (insertionIndex === 0) {
            // supplied threshold is below the lowest threshold
            // assume that this threshold classifies all records as positives
            return _getAllPositiveMatrix(tns, tps, fns, fps, threshold);
        }
        else if (insertionIndex === thresholds.length) {
            // supplied threshold is above the highest threshold
            // assume that this threshold classifies all records as negatives
            return _getAllNegativeMatrix(tns, tps, fns, fps, threshold);
        }
        else {
            // supplied threshold is between two thresholds
            // compare the supplied threshold to the midpoint of the two thresholds
            var midpoint = (thresholds[insertionIndex] + thresholds[insertionIndex - 1]) / 2;
            if (threshold >= midpoint) {
                // upper threshold is nearer
                return _getConfusionMatrixByIndex(insertionIndex, tns, tps, fns, fps, threshold);
            }
            else {
                // lower threshold is nearer
                return _getConfusionMatrixByIndex(insertionIndex - 1, tns, tps, fns, fps, threshold);
            }
        }
    }
}
exports.getConfusionMatrixByThreshold = getConfusionMatrixByThreshold;
function getOptimalThresholdByCostRatio(confusionMatrices, costRatio) {
    if (!confusionMatrices || !confusionMatrices.thresholds || costRatio < 0) {
        return undefined;
    }
    var minCost = undefined;
    var threshold = 0.0;
    var thresholds = confusionMatrices.thresholds, fps = confusionMatrices.fps, fns = confusionMatrices.fns;
    for (var i = 0; i < thresholds.length; i++) {
        var cost = costRatio * fps[i] + 1.0 * fns[i];
        if (cost !== undefined && !isNaN(cost) && (minCost === undefined || cost < minCost)) {
            minCost = cost;
            threshold = thresholds[i];
        }
    }
    var extremeThresholds = [0, 1];
    for (var _i = 0, extremeThresholds_1 = extremeThresholds; _i < extremeThresholds_1.length; _i++) {
        var t = extremeThresholds_1[_i];
        var cm = getConfusionMatrixByThreshold(confusionMatrices, t);
        if (cm) {
            var cost = costRatio * cm.falsePositive + 1.0 * cm.falseNegative;
            if (cost !== undefined && !isNaN(cost) && (minCost === undefined || cost < minCost)) {
                minCost = cost;
                threshold = t;
            }
        }
    }
    return { value: minCost, threshold: threshold };
}
exports.getOptimalThresholdByCostRatio = getOptimalThresholdByCostRatio;
function _findMaxClassification(confusionMatrices, confusionMatricesClassification, property) {
    var normalizedThreshold = confusionMatrices.thresholds.map(function (t) { return Math.round(t * 1000) / 1000; });
    // prepare the result and later find the classification by maximum criteria
    var propertyCollection = confusionMatricesClassification.map(function (n) { return n[property]; }).filter(Boolean);
    var maxValue = Math.max.apply(Math, propertyCollection);
    return confusionMatricesClassification.find(function (n) {
        return n.threshold !== null && n[property] === maxValue && normalizedThreshold.includes(n.threshold);
    });
}
var getConfusionMatricesClassification = function (confusionMatrices) {
    if (!confusionMatrices) {
        return [];
    }
    var confusionMatricesClassification = [];
    for (var i = 0; i < ModelMetrics_1.THRESHOLDS_SIZE; i += 1) {
        var threshold = i / ModelMetrics_1.THRESHOLDS_SIZE;
        var classification = getConfusionMatrixByThreshold(confusionMatrices, threshold);
        if (classification) {
            confusionMatricesClassification.push(classification);
        }
    }
    return confusionMatricesClassification;
};
exports.getConfusionMatricesClassification = getConfusionMatricesClassification;
var getDefaultThreshold = function (confusionMatrices, confusionMatricesClassification, optimization) {
    var _a;
    var classification = undefined;
    switch (optimization) {
        case ModelMetrics_1.ThresholdOptimizeCategory.ACCURACY:
            classification = _findMaxClassification(confusionMatrices, confusionMatricesClassification, 'accuracy');
            break;
        case ModelMetrics_1.ThresholdOptimizeCategory.F1SCORE:
            classification = _findMaxClassification(confusionMatrices, confusionMatricesClassification, 'f1Score');
            break;
        case ModelMetrics_1.ThresholdOptimizeCategory.INFORMEDNESS:
            classification = _findMaxClassification(confusionMatrices, confusionMatricesClassification, 'informedness');
            break;
        case ModelMetrics_1.ThresholdOptimizeCategory.MARKEDNESS:
            classification = _findMaxClassification(confusionMatrices, confusionMatricesClassification, 'markedness');
            break;
        case ModelMetrics_1.ThresholdOptimizeCategory.MCC:
            classification = _findMaxClassification(confusionMatrices, confusionMatricesClassification, 'matthewsCorrelationCoefficient');
            break;
        case ModelMetrics_1.ThresholdOptimizeCategory.PRECISION:
            classification = _findMaxClassification(confusionMatrices, confusionMatricesClassification, 'positivePredictiveValue');
            break;
    }
    return (_a = classification === null || classification === void 0 ? void 0 : classification.threshold) !== null && _a !== void 0 ? _a : null;
};
exports.getDefaultThreshold = getDefaultThreshold;
//# sourceMappingURL=ConfusionMatricesUtils.js.map